import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  S4: "#ef4444",
  S3: "#f59e0b",
  S2: "#3b82f6",
  S1: "#10b981",
  P1: "#8b5cf6",
  OPEN: "#ef4444",
  ACKNOWLEDGED: "#f59e0b",
  IN_PROGRESS: "#3b82f6",
  RESOLVED: "#10b981",
  CLOSED: "#71717a",
};

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/analytics/summary?days=${days}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days]);

  const formatCategoryData = (byCategory) => {
    return Object.entries(byCategory || {}).map(([key, value]) => ({
      name: key.replace(/_/g, " ").substring(0, 12),
      value,
    }));
  };

  const formatSeverityData = (bySeverity) => {
    return Object.entries(bySeverity || {}).map(([key, value]) => ({
      name: key,
      value,
      fill: COLORS[key] || "#71717a",
    }));
  };

  const formatStatusData = (byStatus) => {
    return Object.entries(byStatus || {}).map(([key, value]) => ({
      name: key,
      value,
      fill: COLORS[key] || "#71717a",
    }));
  };

  const formatDeptData = (byDept) => {
    return Object.entries(byDept || {}).map(([key, value]) => ({
      name: key,
      issues: value,
    }));
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">
              Analytics
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Operational intelligence insights
            </p>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px] bg-background" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Trend */}
          <Card className="orbit-card lg:col-span-2" data-testid="daily-trend-chart">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Daily Issue Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.daily_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a" 
                      fontSize={10}
                      tickFormatter={(value) => value.slice(5)}
                    />
                    <YAxis stroke="#71717a" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#18181b", 
                        border: "1px solid #27272a",
                        borderRadius: "4px"
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#eab308" 
                      strokeWidth={2}
                      dot={{ fill: "#eab308" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* By Severity */}
          <Card className="orbit-card" data-testid="severity-chart">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Issues by Severity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatSeverityData(data?.by_severity)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {formatSeverityData(data?.by_severity).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#18181b", 
                        border: "1px solid #27272a",
                        borderRadius: "4px"
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* By Status */}
          <Card className="orbit-card" data-testid="status-chart">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Issues by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatStatusData(data?.by_status)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {formatStatusData(data?.by_status).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#18181b", 
                        border: "1px solid #27272a",
                        borderRadius: "4px"
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* By Department */}
          <Card className="orbit-card" data-testid="department-chart">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Issues by Department
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formatDeptData(data?.by_department)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis type="number" stroke="#71717a" fontSize={10} />
                    <YAxis dataKey="name" type="category" stroke="#71717a" fontSize={10} width={40} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#18181b", 
                        border: "1px solid #27272a",
                        borderRadius: "4px"
                      }} 
                    />
                    <Bar dataKey="issues" fill="#eab308" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* By Category */}
          <Card className="orbit-card" data-testid="category-chart">
            <CardHeader>
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Issues by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formatCategoryData(data?.by_category)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="name" stroke="#71717a" fontSize={8} angle={-45} textAnchor="end" height={60} />
                    <YAxis stroke="#71717a" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#18181b", 
                        border: "1px solid #27272a",
                        borderRadius: "4px"
                      }} 
                    />
                    <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics;
